import React from 'react';
import { Sliders, Shield } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент панели быстрой настройки параметров генерации хеша
 */
const OptionsPanel = ({ 
  formData,
  setFormData,
  errors,
  isGenerating
}) => {
  // Быстрая установка предустановленного количества раундов
  const setRounds = (rounds) => {
    setFormData(prev => ({
      ...prev,
      rounds
    }));
  };
  
  // Быстрая настройка генератора паролей
  const setPasswordGenerator = (enabled, length = 12) => {
    setFormData(prev => ({
      ...prev,
      autoGenerate: enabled,
      passwordLength: length,
      includeLowercase: true,
      includeUppercase: true,
      includeNumbers: true,
      includeSymbols: enabled ? prev.includeSymbols : false
    }));
  };
  
  // Выбор профиля безопасности
  const selectSecurityProfile = (profile) => {
    switch (profile) {
      case 'standard':
        setFormData(prev => ({
          ...prev,
          rounds: 10,
          hashFormat: 'bcrypt',
          autoGenerate: false
        }));
        break;
      case 'enhanced':
        setFormData(prev => ({
          ...prev,
          rounds: 12,
          hashFormat: 'bcrypt',
          autoGenerate: true,
          passwordLength: 16,
          includeLowercase: true,
          includeUppercase: true,
          includeNumbers: true,
          includeSymbols: true
        }));
        break;
      case 'maximum':
        setFormData(prev => ({
          ...prev,
          rounds: 14,
          hashFormat: 'bcrypt',
          autoGenerate: true,
          passwordLength: 24,
          includeLowercase: true,
          includeUppercase: true,
          includeNumbers: true,
          includeSymbols: true
        }));
        break;
      default:
        break;
    }
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sliders className="w-5 h-5 mr-2 text-blue-500" />
        Быстрые настройки
      </h3>
      
      <div className="space-y-5">
        {/* Профили безопасности */}
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Профили безопасности
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => selectSecurityProfile('standard')}
              className={`p-3 text-center text-sm rounded-lg border transition-colors
                ${formData.rounds === 10 && !formData.autoGenerate
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium mb-1">Стандартный</div>
              <div className="text-xs opacity-75">10 раундов</div>
            </button>
            
            <button
              type="button"
              onClick={() => selectSecurityProfile('enhanced')}
              className={`p-3 text-center text-sm rounded-lg border transition-colors
                ${formData.rounds === 12 && formData.autoGenerate && formData.passwordLength === 16
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium mb-1">Усиленный</div>
              <div className="text-xs opacity-75">12 раундов</div>
            </button>
            
            <button
              type="button"
              onClick={() => selectSecurityProfile('maximum')}
              className={`p-3 text-center text-sm rounded-lg border transition-colors
                ${formData.rounds === 14 && formData.autoGenerate && formData.passwordLength === 24
                  ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium mb-1">Максимальный</div>
              <div className="text-xs opacity-75">14 раундов</div>
            </button>
          </div>
        </div>
        
        {/* Настройка количества раундов */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">
              Количество раундов
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.rounds}
            </span>
          </div>
          <input
            type="range"
            min="4"
            max="20"
            step="1"
            value={formData.rounds}
            onChange={(e) => setRounds(parseInt(e.target.value, 10))}
            aria-label="Количество раундов"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          
          {/* Кнопки с предустановленными значениями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[4, 8, 10, 12].map(rounds => (
              <button
                key={rounds}
                type="button"
                onClick={() => setRounds(rounds)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.rounds === rounds
                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rounds}
              </button>
            ))}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Больше раундов = выше безопасность, но дольше генерация
          </div>
        </div>
        
        {/* Настройка генератора паролей */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">
              Генератор паролей
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setPasswordGenerator(false)}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${!formData.autoGenerate
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Выключен</div>
              <div className="text-xs opacity-75">Ручной ввод</div>
            </button>
            
            <button
              type="button"
              onClick={() => setPasswordGenerator(true)}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.autoGenerate
                  ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Включен</div>
              <div className="text-xs opacity-75">Автогенерация</div>
            </button>
          </div>
        </div>
        
        {/* Длина пароля (только если генератор включен) */}
        {formData.autoGenerate && (
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label className="text-gray-600 dark:text-gray-400">
                Длина пароля
              </label>
              <span className="font-medium text-gray-900 dark:text-white">
                {formData.passwordLength} символов
              </span>
            </div>
            <input
              type="range"
              min="6"
              max="32"
              step="2"
              value={formData.passwordLength}
              onChange={(e) => setFormData(prev => ({ ...prev, passwordLength: e.target.value }))}
              aria-label="Длина пароля"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            
            {/* Кнопки с предустановленными значениями */}
            <div className="grid grid-cols-4 gap-1 mt-2">
              {[8, 12, 16, 24].map(length => (
                <button
                  key={length}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, passwordLength: length }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${parseInt(formData.passwordLength, 10) === length
                      ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  {length}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Кнопка генерации хеша */}
        <Button
          primary
          fullWidth
          onClick={() => document.querySelector('form').requestSubmit()}
          icon={<Shield className="w-4 h-4" />}
          disabled={isGenerating || Object.keys(errors).length > 0}
          isLoading={isGenerating}
        >
          Сгенерировать хеш
        </Button>
      </div>
    </div>
  );
};

export default OptionsPanel;